import type { PropsWithChildren } from 'react';

import { LoaderTraceLogo } from '@stratumn/atomic';

import { cn } from '@/shadcn/lib/utils';
import { useTheme } from '@/shadcn/theme/theme-provider';

import { useWorkspaceHighlight } from '@/hooks/useWorkspaceHighlight';

type Props = PropsWithChildren<{
  loading?: boolean;
}>;

export const KanbanMain = ({ children, loading = false }: Props) => {
  const { theme } = useTheme();
  // A workspace-scoped workflow swaps the purple map backdrop for a plain
  // card surface (--card: white in light mode, dark-adapted in dark). Keyed
  // on the workspace existing — matching the navbar's workspace tag — not on
  // its color being usable. While the lookup is in flight the map is held
  // back (and the hook keeps the last settled state), so the backdrop settles
  // in one paint instead of flashing the purple map before going white.
  const { inWorkspace, loading: workspaceLoading } = useWorkspaceHighlight();
  const showMap = !(inWorkspace || workspaceLoading);
  return (
    <>
      <div
        aria-busy={loading}
        className={cn(
          'linear 250ms fixed top-full-navbar right-[var(--workspace-frame-headroom,0px)] left-0 z-2 box-border flex h-[calc(100%-var(--spacing-full-navbar)-var(--workspace-frame-headroom,0px))] overflow-auto pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] opacity-100 transition-opacity duration-250',
          loading && 'opacity-0'
        )}
      >
        {children}
      </div>
      <div
        className={cn(
          'fixed top-full-navbar right-0 bottom-0 left-0 overflow-hidden',
          inWorkspace ? 'bg-card' : 'bg-primary-dark'
        )}
        data-testid="kanban-backdrop"
        role="none"
      >
        <div
          className={cn(
            'h-full',
            showMap &&
              "bg-[url('/src/assets/IndigoTrace_Map.svg')] bg-center bg-cover bg-primary-dark bg-no-repeat dark:bg-background dark:brightness-50"
          )}
          role="none"
        >
          <div
            className={cn(
              'flex h-full items-center justify-center opacity-0 transition-opacity duration-250',
              loading && 'opacity-100'
            )}
          >
            <LoaderTraceLogo light={theme === 'dark'} />
          </div>
        </div>
      </div>
    </>
  );
};
